import { useCallback, useEffect, useMemo, useState } from 'react'
import { ForgeSelect, ForgeOption, ForgeSwitch } from '@tylertech/forge-react'
import { updateProfile } from '../api/users'
import useProfile from '../hooks/useProfile'
import { queryClient } from '../queryClient'
import { STATES } from '../utils/states'
import './EditProfile.css'

const emptyProfile = {
  firstName: '',
  lastName: '',
  preferredName: '',
  email: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  addressCity: '',
  addressState: '',
  addressZipCode: '',
  addressCountry: '',
  altAddressSameAsPrimary: false,
  altAddressLine1: '',
  altAddressCity: '',
  altAddressState: '',
  altAddressZipCode: '',
}

export default function EditProfile() {
  const requiredFields = useMemo(() => new Set(['firstName', 'lastName']), [])
  const [profile, setProfile] = useState(emptyProfile)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [fieldErrors, setFieldErrors] = useState({})
  const [touched, setTouched] = useState({})
  const { profile: cachedProfile, isLoading: isProfileLoading, refetch: refetchProfile } = useProfile()

  useEffect(() => {
    if (cachedProfile) {
      setProfile({ ...emptyProfile, ...cachedProfile, phone: formatPhoneForInput(cachedProfile.phone || '') })
    }
  }, [cachedProfile])

  const formatPhoneForInput = (value) => {
    const digits = value.replace(/[^\d]/g, '')
    if (!digits) return ''

    // Assume US format; preserve leading 1 if present
    const country = digits.startsWith('1') ? '1' : ''
    const core = country ? digits.slice(1) : digits

    const part1 = core.slice(0, 3)
    const part2 = core.slice(3, 6)
    const part3 = core.slice(6, 10)

    let formatted = country ? `+${country}` : ''
    if (part1) formatted += ` (${part1}`
    if (part1 && part1.length === 3) formatted += ')'
    if (part2) formatted += ` ${part2}`
    if (part3) formatted += `-${part3}`
    return formatted.trim()
  }

  const handleChange = (field, value, inputType) => {
    let nextValue = value
    if (field === 'phone') {
      const prevDigits = (profile.phone || '').replace(/[^\d]/g, '')
      let digits = value.replace(/[^\d]/g, '')
      // A backspace/delete that only removed a formatting char (paren/space/dash)
      // leaves the digit count unchanged; drop the trailing digit so deletion can
      // progress past "(123)" instead of the formatter re-adding the deleted char.
      if (inputType && inputType.startsWith('delete') && digits === prevDigits) {
        digits = digits.slice(0, -1)
      }
      nextValue = formatPhoneForInput(digits)
    }

    if (field === 'addressZipCode') {
      nextValue = value.slice(0, 7)
    }

    setTouched((prev) => ({
      ...prev,
      [field]: prev[field] || requiredFields.has(field),
    }))
    setProfile((prev) => ({ ...prev, [field]: nextValue }))
    if (touched[field] || requiredFields.has(field)) {
      validateField(field, nextValue)
    }
  }

  const validatePhone = (value) => {
    const trimmed = (value || '').trim()
    const digits = trimmed.replace(/[^\d]/g, '')
    const normalizedLength = digits.startsWith('1') ? digits.length - 1 : digits.length
    if (digits.length > 0 && normalizedLength !== 10) {
      return 'Enter a 10-digit phone number'
    }
    return ''
  }

  const validateName = (value, label) => {
    const trimmed = (value || '').trim()
    if (!trimmed) return `${label} is required`
    if (trimmed.length > 50) return `${label} must be 50 characters or fewer`
    return ''
  }

  const validateField = (field, value) => {
    const trimmed = (value || '').trim()
    let message = ''
    if (field === 'firstName') {
      message = validateName(trimmed, 'Legal first name')
    } else if (field === 'lastName') {
      message = validateName(trimmed, 'Legal last name')
    } else if (field === 'phone') {
      message = validatePhone(trimmed)
    }
    setFieldErrors((prev) => {
      const next = { ...prev }
      if (message) {
        next[field] = message
      } else {
        delete next[field]
      }
      return next
    })

    return message
  }

  const validateAddress = (data, fields) => {
    const values = fields.map(({ name }) => (data[name] || '').trim())
    const hasAny = values.some(Boolean)
    if (!hasAny) return {}

    const errors = {}
    fields.forEach(({ name, label }, i) => {
      if (!values[i]) {
        errors[name] = `All address fields must be completed, ${label} is missing`
      }
    })
    return errors
  }

  const primaryAddressValidation = [
    { name: 'addressLine1', label: 'Address' },
    { name: 'addressCity', label: 'City' },
    { name: 'addressState', label: 'State' },
    { name: 'addressZipCode', label: 'Zip Code' },
  ]

  const altAddressValidation = [
    { name: 'altAddressLine1', label: 'Address' },
    { name: 'altAddressCity', label: 'City' },
    { name: 'altAddressState', label: 'State' },
    { name: 'altAddressZipCode', label: 'Zip Code' },
  ]

  const validateProfile = (data) => {
    const nextErrors = {}
    const firstNameMessage = validateName(data.firstName, 'Legal first name')
    if (firstNameMessage) {
      nextErrors.firstName = firstNameMessage
    }
    const lastNameMessage = validateName(data.lastName, 'Legal last name')
    if (lastNameMessage) {
      nextErrors.lastName = lastNameMessage
    }
    const phoneMessage = validatePhone(data.phone)
    if (phoneMessage) {
      nextErrors.phone = phoneMessage
    }
    Object.assign(nextErrors, validateAddress(data, primaryAddressValidation))
    if (!data.altAddressSameAsPrimary) {
      Object.assign(nextErrors, validateAddress(data, altAddressValidation))
    }
    setFieldErrors(nextErrors)
    return nextErrors
  }

  const handleSubmit = useCallback(
    async (eventOrCallback) => {
      if (eventOrCallback?.preventDefault) {
        eventOrCallback.preventDefault()
      }
      const onSuccess = eventOrCallback?.detail?.onSuccess
      if (isSaving) return

      const validationErrors = validateProfile(profile)
      if (Object.keys(validationErrors).length > 0) {
        return
      }

      window.dispatchEvent(
        new CustomEvent('edit-profile:status', {
          detail: {
            label: 'Updating...',
            disabled: true,
          },
        }),
      )

      setIsSaving(true)
      setError('')
      setSuccess('')
      try {
        const updated = await updateProfile(profile)
        setProfile({ ...emptyProfile, ...updated })
        setSuccess('Profile saved successfully.')
        queryClient.invalidateQueries({ queryKey: ['profile'] })
        refetchProfile()
        if (typeof onSuccess === 'function') {
          onSuccess()
        }
      } catch (err) {
        setError('Unable to save your profile right now.')
      } finally {
        setIsSaving(false)
        window.dispatchEvent(
          new CustomEvent('edit-profile:status', {
            detail: {
              label: null,
              disabled: false,
            },
          }),
        )
      }
    },
    [isSaving, profile, refetchProfile],
  )

  useEffect(() => {
    const saveListener = (event) => {
      handleSubmit(event)
    }
    window.addEventListener('edit-profile:save', saveListener)
    return () => window.removeEventListener('edit-profile:save', saveListener)
  }, [handleSubmit])

  const profileInfoFields = useMemo(
    () => [
      { label: 'Legal First Name', name: 'firstName', colSpan: 2, maxLength: 50 },
      { label: 'Legal Last Name', name: 'lastName', colSpan: 2, maxLength: 50 },
      { label: 'Preferred Name', name: 'preferredName', colSpan: 2, maxLength: 50 },
    ],
    [],
  )

  const contactFields = useMemo(
    () => [
      { label: 'Email Address', name: 'email', colSpan: 2, readOnly: true },
      { label: 'Phone Number', name: 'phone', colSpan: 2 },
    ],
    [],
  )

  const addressFields = useMemo(
    () => [
      { label: 'Street Address', name: 'addressLine1', colSpan: 4 },
      { label: 'City/Town', name: 'addressCity', colSpan: 1 },
      { label: 'State', name: 'addressState', colSpan: 1, type: 'select', options: STATES },
      { label: 'Zip Code', name: 'addressZipCode', colSpan: 1, maxLength: 7 },
    ],
    [],
  )

  const altAddressFields = useMemo(
    () => [
      { label: 'Street Address', name: 'altAddressLine1', colSpan: 4 },
      { label: 'City/Town', name: 'altAddressCity', colSpan: 1 },
      { label: 'State', name: 'altAddressState', colSpan: 1, type: 'select', options: STATES },
      { label: 'Zip Code', name: 'altAddressZipCode', colSpan: 1, maxLength: 7 },
    ],
    [],
  )

  const handleSameAsPrimaryChange = () => {
    setProfile((prev) => {
      const toggled = !prev.altAddressSameAsPrimary
      if (toggled) {
        return {
          ...prev,
          altAddressSameAsPrimary: true,
          altAddressLine1: prev.addressLine1,
          altAddressCity: prev.addressCity,
          altAddressState: prev.addressState,
          altAddressZipCode: prev.addressZipCode,
        }
      }
      return {
        ...prev,
        altAddressSameAsPrimary: false,
      }
    })
  }

  const renderField = (field, idx) => {
    const fieldError = field.name ? fieldErrors[field.name] : ''
    const isInvalid = Boolean(fieldError)
    const inputId = field.name ? `edit-profile-${field.name}` : undefined
    const labelId = field.name ? `edit-profile-label-${field.name}` : undefined
    const errorId = field.name ? `edit-profile-error-${field.name}` : undefined

    if (field.readOnly) {
      return (
        <div
          key={`${field.label}-${idx}`}
          className="profile-edit__field"
          style={{ '--grid-span': field.colSpan }}>
          <label className="profile-edit__label" id={labelId}>
            <span>{field.label}</span>
          </label>
          <input
            className="profile-edit__input"
            id={inputId}
            value={profile[field.name] || ''}
            disabled
            aria-label={field.label}
          />
        </div>
      )
    }

    if (field.type === 'select') {
      return (
        <div
          key={`${field.label}-${idx}`}
          className={`profile-edit__field${isInvalid ? ' profile-edit__field--error' : ''}`}
          style={{ '--grid-span': field.colSpan }}>
          <label className="profile-edit__label" id={labelId} htmlFor={inputId}>
            {field.required ? <span className="profile-edit__required">*</span> : null}
            <span>{field.label}</span>
          </label>
          <ForgeSelect
            id={inputId}
            name={field.name}
            value={profile[field.name] || ''}
            on-change={(evt) => handleChange(field.name, evt.detail)}
            aria-invalid={isInvalid}
            aria-labelledby={labelId}
            aria-describedby={isInvalid ? errorId : undefined}
            aria-label={field.label}
            disabled={isProfileLoading || isSaving}>
            <ForgeOption value="">-- Select --</ForgeOption>
            {field.options.map((option) => (
              <ForgeOption key={option.value} value={option.value}>
                {option.label}
              </ForgeOption>
            ))}
          </ForgeSelect>
          {isInvalid ? (
            <div className="profile-edit__error-text" id={errorId}>
              {fieldError}
            </div>
          ) : null}
        </div>
      )
    }

    return (
      <div
        key={`${field.label}-${idx}`}
        className={`profile-edit__field${isInvalid ? ' profile-edit__field--error' : ''}`}
        style={{ '--grid-span': field.colSpan }}>
        <label className="profile-edit__label" id={labelId} htmlFor={inputId}>
          {field.required ? <span className="profile-edit__required">*</span> : null}
          <span>{field.label}</span>
        </label>
        <input
          className={`profile-edit__input${isInvalid ? ' profile-edit__input--error' : ''}`}
          id={inputId}
          name={field.name}
          value={profile[field.name] || ''}
          onInput={(evt) => handleChange(field.name, evt.target.value, evt.nativeEvent?.inputType)}
          onBlur={(evt) => {
            setTouched((prev) => ({ ...prev, [field.name]: true }))
            validateField(field.name, evt.target.value)
          }}
          aria-invalid={isInvalid}
          aria-labelledby={labelId}
          aria-describedby={isInvalid ? errorId : undefined}
          aria-label={field.label}
          disabled={isProfileLoading || isSaving}
          type={field.name === 'phone' ? 'tel' : 'text'}
          inputMode={field.name === 'phone' ? 'tel' : undefined}
          pattern={field.name === 'phone' ? '[0-9+]*' : undefined}
          maxLength={field.maxLength}
        />
        {isInvalid ? (
          <div className="profile-edit__error-text" id={errorId}>
            {fieldError}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <form className="m-6 profile-edit__form" onSubmit={handleSubmit}>
      {error ? <p className="edit-form__status edit-form__status--error">{error}</p> : null}
      {success ? <p className="edit-form__status edit-form__status--success">{success}</p> : null}
      {Object.keys(fieldErrors).length > 0 ? (
        <ul className="edit-form__error-list">
          {Object.values(fieldErrors).map((msg, i) => (
            <li key={i}>{msg}</li>
          ))}
        </ul>
      ) : null}

      <section className="profile-section">
        <h3 className="section-header">Profile Information</h3>
        <div className="profile-grid mt-6">{profileInfoFields.map(renderField)}</div>
      </section>

      <section className="profile-section">
        <h3 className="section-header">Contact Details</h3>
        <div className="profile-grid mt-6">{contactFields.map(renderField)}</div>
      </section>

      <section className="profile-section">
        <h3 className="section-header">Address</h3>

        <h4 className="profile-edit__section-heading">Primary Residence Address</h4>
        <div className="profile-grid mt-6">{addressFields.map(renderField)}</div>

        <div className="profile-edit__checkbox-row">
          <ForgeSwitch
            checked={profile.altAddressSameAsPrimary}
            on-change={handleSameAsPrimaryChange}
            disabled={isSaving}>
            Address Same as Primary
          </ForgeSwitch>
        </div>

        <h4 className="profile-edit__section-heading">Alternate Contact Address</h4>
        <div className="profile-grid mt-6">
          {altAddressFields.map((field, idx) =>
            renderField(
              profile.altAddressSameAsPrimary ? { ...field, readOnly: true } : field,
              idx,
            )
          )}
        </div>
      </section>

    </form>
  )
}
